import React from "react";
import { toast } from "react-toastify";
import "./PublicLinkUrlBox.css";

// Shared "here is the full public link" box -- reused by PublicLinkSummaryCell
// (single-recipient table cell), ManageAssignedUsersModal (per-assignee row),
// and JustAssignedLinksModal (right after creating an assignment). Always
// renders the EXACT `url` the backend returned (services/
// publicFormTokenService.js#buildPublicFormUrl -- an absolute URL built from
// APP_URL, never a bare "/form/f/<token>" path) -- never reconstructed
// client-side, never truncated for Copy/Open, only visually truncated via
// CSS ellipsis with the full value on hover (`title`).
//
// This is deliberately IN-MEMORY, per-session state (see each caller's own
// `generatedLinks`/`links` prop) -- only public_token_hash is ever persisted
// server-side, so after a page refresh there is nothing left to show here;
// callers stop rendering this box once their local "just generated" state is
// gone, which is correct, not a bug (§7 of the Phase 16 design).
export default function PublicLinkUrlBox({ url, className }) {
  if (!url) return null;

  const handleCopy = async () => {
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        copied = true;
      }
    } catch (err) {
      copied = false;
    }
    if (!copied) {
      try {
        const el = document.createElement("textarea");
        el.value = url;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        copied = true;
      } catch (err) {
        copied = false;
      }
    }
    toast[copied ? "success" : "error"](copied ? "Link copied" : "Could not copy — copy it manually");
  };

  const handleOpen = () => window.open(url, "_blank", "noopener,noreferrer");

  return (
    <div className={`plub-box${className ? ` ${className}` : ""}`}>
      <input
        type="text"
        readOnly
        value={url}
        title={url}
        aria-label="Public form link"
        className="plub-input"
        onFocus={(e) => e.target.select()}
      />
      <div className="plub-actions">
        <button type="button" className="plub-btn" onClick={handleCopy}>Copy Link</button>
        <button type="button" className="plub-btn plub-btn-open" onClick={handleOpen}>Open Link</button>
      </div>
    </div>
  );
}
